import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import { isSuperAdmin } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Users } from "lucide-react";

export default function Organizations() {
  const t = useT();
  const qc = useQueryClient();
  const { roles } = useOutletContext();
  const canCreate = isSuperAdmin(roles);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState("business");
  const [busy, setBusy] = useState(false);

  const orgs = useQuery({
    queryKey: ["admin-orgs"],
    queryFn: async () => (await api.get("/platform/organizations")).data,
    retry: false,
  });

  const items = Array.isArray(orgs.data) ? orgs.data : orgs.data?.organizations || orgs.data?.items || [];

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/platform/organizations", {
        name: name.trim(),
        type: orgType,
        organisation_type: orgType,
      });
      setName("");
      setOpen(false);
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-orgs"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.organisation")}
        title={t("nav.organisation")}
        subtitle={t("platform.orgOverview")}
        actions={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="orgs-create-open" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                  <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="orgs-create-dialog">
                <DialogHeader>
                  <DialogTitle>{t("nav.organisation")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label>{t("common.name")}</Label>
                    <Input data-testid="orgs-create-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-cream-50" />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <div className="flex gap-2 mt-1.5">
                      {["business", "care", "internal"].map((o) => (
                        <button
                          key={o}
                          type="button"
                          onClick={() => setOrgType(o)}
                          data-testid={`orgs-type-${o}`}
                          className={`rounded-full px-4 py-2 text-sm border ${orgType === o ? "bg-sage-500 text-white border-sage-500" : "bg-white border-border text-ink-700"}`}
                        >
                          {o === "business" ? t("modes.business") : o === "care" ? t("modes.care") : "Platform"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} data-testid="orgs-create-submit" disabled={busy} className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
                    {busy ? t("common.saving") : t("common.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {orgs.isLoading && <LoadingCard />}
      {orgs.isError && <ErrorState message={errMsg(orgs.error)} onRetry={() => orgs.refetch()} />}
      {!orgs.isLoading && !orgs.isError && items.length === 0 && (
        <EmptyState icon={Building2} title={t("common.empty")} testId="orgs-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="orgs-list">
        {items.map((o, i) => {
          const otype = String(o.type || o.organisation_type || o.organization_type || "business").toLowerCase();
          const isCare = otype === "care" || otype === "ggz";
          return (
            <div key={o.id || i} className="rounded-3xl bg-white shadow-card p-6" data-testid={`orgs-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className={`h-11 w-11 rounded-2xl flex items-center justify-center ${isCare ? "bg-accents-care/10" : "bg-sage-100"}`}>
                  <Building2 className={`h-5 w-5 ${isCare ? "text-accents-care" : "text-sage-700"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-ink-900 truncate">{o.name || "—"}</div>
                  <div className="text-xs text-ink-500 mt-0.5 flex items-center gap-1.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${isCare ? "bg-accents-care/10 text-accents-care" : otype === "internal" ? "bg-sage-100 text-sage-700" : "bg-amber-100 text-amber-800"}`}>
                      {isCare ? "Care" : otype === "internal" ? "Platform" : "Business"}
                    </span>
                    · {o.plan || o.tier || "standard"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-ink-500">
                <Users className="h-3.5 w-3.5" />
                <span>{o.members_count ?? o.member_count ?? "—"} members</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
